import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Divider,
	forwardRef,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Portal,
	RadioGroup,
	Stack,
	StackDivider,
	StyleProps,
	Tooltip,
} from '@bonusly/design-system';

import { usePrivacy } from '../components/privacy/hooks/use-privacy';
import { PrivacyOption } from '../components/privacy/privacy-option';
import { useActionInput } from '../context';
import { useIsEnabled } from '../hooks';
import { usePrivacyTooltipText } from './privacy/hooks/use-privacy-tooltip-text';
import { PrivacyButton } from './privacy/privacy-button';

type ActionPrivacyProps = StyleProps;

export const ActionPrivacy = forwardRef<ActionPrivacyProps, 'button'>((props, ref) => {
	const { privacy, setPrivacy } = useActionInput();
	const { options } = usePrivacy();
	const shouldRender = useIsEnabled('privacy');
	const { t } = useTranslation('action_input');
	const { startDraft } = useActionInput();

	if (!shouldRender) {
		return null;
	}

	return (
		<Popover isLazy onOpen={startDraft} placement="bottom-end" returnFocusOnClose={false}>
			{({ onClose }) => (
				<>
					<ButtonWrapper>
						<PrivacyButton ref={ref} {...props} />
					</ButtonWrapper>

					<Portal>
						<PopoverContent borderColor="bg.subtle" boxShadow="xl" w="sm">
							<Box color="black" fontSize="lg" p={3} pl={4} pt={4}>
								{t('privacy.privacy_select_title')}
							</Box>
							<Divider borderColor="gray.50" />
							<RadioGroup defaultValue={privacy}>
								<Stack divider={<StackDivider borderColor="gray.50" />} spacing={0}>
									{options.map((option) => (
										<PrivacyOption
											key={option.value}
											option={option}
											onChange={() => {
												setPrivacy(option.value);
												onClose();
											}}
										/>
									))}
								</Stack>
							</RadioGroup>
						</PopoverContent>
					</Portal>
				</>
			)}
		</Popover>
	);
});

const ButtonWrapper: FC<PropsWithChildren> = ({ children }) => {
	const { buttonEnabled } = usePrivacy();
	const tooltipText = usePrivacyTooltipText();

	if (buttonEnabled) {
		return <PopoverTrigger>{children}</PopoverTrigger>;
	} else {
		return (
			<Tooltip as="span" display="block" label={tooltipText} placement="bottom">
				{children}
			</Tooltip>
		);
	}
};

ActionPrivacy.displayName = 'ActionPrivacy';
